import { format } from "date-fns";

/**
 * Format date as MM/DD/YYYY
 * Used for displaying dates in DataVault components
 */
export const toMMDDYYYYFormat = (dateTime: Date): string => {
  return format(dateTime, "MM/dd/yyyy");
};

/**
 * Parse a date-only string (YYYY-MM-DD) as a local date without timezone conversion.
 * This prevents the common issue where "2023-12-25" becomes December 24 in timezones behind UTC.
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object representing the local date, or null if invalid
 */
export const parseDateOnlyString = (dateString: string): Date | null => {
  if (!dateString) return null;

  // Match YYYY-MM-DD format
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const [, year, month, day] = match;
  const yearNum = parseInt(year, 10);
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);

  // Validate ranges
  if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
    return null;
  }

  // Create date using local timezone (month is 0-based in Date constructor)
  const date = new Date(yearNum, monthNum - 1, dayNum);

  // Verify the date is valid and matches input (handles invalid dates like Feb 30)
  if (
    date.getFullYear() !== yearNum ||
    date.getMonth() !== monthNum - 1 ||
    date.getDate() !== dayNum
  ) {
    return null;
  }

  return date;
};

/**
 * Format a Date object as YYYY-MM-DD string using local date components.
 * This ensures consistent date formatting without timezone conversion issues.
 *
 * @param date - Date object to format
 * @returns Date string in YYYY-MM-DD format
 */
export const formatDateOnlyString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Extract the date part (YYYY-MM-DD) from a datetime string.
 * Handles datetime strings in the format "YYYY-MM-DD HH:mm:ss" and returns only the date portion.
 *
 * @param dateTimeString - Datetime string in format "YYYY-MM-DD HH:mm:ss"
 * @returns Date string in YYYY-MM-DD format, or null if invalid
 */
export const extractDateFromDateTime = (dateTimeString: string): string | null => {
  if (!dateTimeString) return null;

  // Match YYYY-MM-DD from the beginning of the string
  const match = dateTimeString.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
};
